import { ArrowForward, OpenInNew } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import AppCheckbox from 'components/FormElements/AppCheckBox';
import {
  AccountingPeriodChecklist,
  PeriodChecklistCounts,
} from 'further-types/lp/accounting-period';
import { CalendarTime } from 'further-ui/utils';
import ChecklistCard from 'components/new/ChecklistCard';
import { Column, Row } from 'components/Layout';
import { optionalItems, requiredItems } from './checklistConfig';
import { ActiveSidebar, PeriodDetail } from './types';
import { useLayoutContext } from 'contexts/LayoutContext';
import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';
import api, { ApiInputs } from 'lib/trpcClient';
import { getQueryKey } from '@trpc/react-query';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  BANK_RECONCILIATION,
  EQUALISATIONS_ADD,
  INCOME_OR_EXPENSE_LIST,
  LP_INVESTMENT_LIST,
  LP_REALISATION_LIST,
} from 'adminConstants/routes';
import { captureException } from '@sentry/react';

const ChecklistItemIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  fontSize: 20,
}));

const SIDE_PANEL_ONLY_TOOLTIP =
  'This item can only be approved or unapproved via the side panel.';

const ZERO_COUNT_TOOLTIP =
  'There are no items to approve, so this step is automatically completed and cannot be unchecked.';

type RouteParams = {
  periodId: string;
};

type Props = {
  period: PeriodDetail;
  isApproved: boolean;
  onOpenSidebar: (sidebar: ActiveSidebar) => void;
};

const formatCount = (
  counts: PeriodChecklistCounts | undefined,
  key: keyof PeriodChecklistCounts,
  formatValue?: (value: number, currency?: string) => string,
  currency?: string,
): string => {
  if (!counts) return '—';
  const value = counts[key];
  if (value === null || value === undefined) return '—';
  return formatValue ? formatValue(value, currency) : String(value);
};

const SIDE_PANEL_ONLY_ITEMS: Array<keyof AccountingPeriodChecklist> = [
  'transactionRates',
  'companyValuations',
  'unrealisedFxGainsAndLosses',
];

type ChecklistCheckboxParams = {
  itemKey: keyof AccountingPeriodChecklist;
  isChecked: boolean;
  disabled: boolean;
};

const PeriodEndReviewChecklist: React.FC<Props> = ({
  period,
  isApproved,
  onOpenSidebar,
}) => {
  const { periodId } = useParams<RouteParams>();
  const { vehicle, selectedVehicleId } = useLayoutContext();
  const { profile } = useCurrentUser();
  const currentUserLabel = profile.data
    ? `${profile.data.firstName} ${profile.data.lastName}`
    : undefined;

  const [checklistCounts] =
    api.accountingPeriod.getPeriodChecklistCounts.useSuspenseQuery({
      vehicleId: selectedVehicleId ?? '',
      id: periodId,
    });

  const [pendingItems, setPendingItems] = useState<
    Set<keyof AccountingPeriodChecklist>
  >(new Set());

  const [linksMenuAnchor, setLinksMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  const allChecklistItemDefs = [...requiredItems, ...optionalItems];

  const utils = api.useUtils();

  const periodInvalidations = [
    getQueryKey(
      api.accountingPeriod.getPeriodById,
      { vehicleId: vehicle?._id ?? '', id: periodId },
      'query',
    ),
    getQueryKey(
      api.accountingPeriod.getPeriodChecklistCounts,
      { vehicleId: vehicle?._id ?? '', id: periodId },
      'query',
    ),
  ];

  type PeriodSnapshot = {
    snapshot?: ReturnType<typeof utils.accountingPeriod.getPeriodById.getData>;
  };

  const isZeroCount = (key: keyof PeriodChecklistCounts): boolean =>
    checklistCounts[key] === 0;

  const confirmChecklistItems =
    api.accountingPeriod.confirmChecklistItems.useMutation(
      useHandleTrpcMutation<
        unknown,
        ApiInputs['accountingPeriod']['confirmChecklistItems'],
        PeriodSnapshot
      >()({
        invalidations: [],
        onMutate: async ({ vehicleId, id, items, confirmed }) => {
          await utils.accountingPeriod.getPeriodById.cancel({ vehicleId, id });
          const snapshot = utils.accountingPeriod.getPeriodById.getData({
            vehicleId,
            id,
          });
          const confirmedAt = confirmed
            ? new CalendarTime(new Date())
            : undefined;
          const confirmedByLabel = confirmed ? currentUserLabel ?? null : null;
          utils.accountingPeriod.getPeriodById.setData(
            { vehicleId, id },
            (old) => {
              if (!old) return old;
              const checklist = { ...old.checklist };
              for (const item of items) {
                checklist[item] = {
                  ...checklist[item],
                  confirmed,
                  confirmedByLabel,
                  confirmedBy: null,
                  confirmedAt,
                };
              }
              return { ...old, checklist };
            },
          );
          return { snapshot };
        },
        onSuccess: (_data, variables) => {
          utils.accountingPeriod.getPeriodById.invalidate({
            vehicleId: variables.vehicleId ?? '',
            id: variables.id,
          });
        },
        onError: (err, variables, context) => {
          captureException(err);
          if (context?.snapshot) {
            utils.accountingPeriod.getPeriodById.setData(
              { vehicleId: variables.vehicleId, id: variables.id },
              context.snapshot,
            );
          }
        },
        onSettled: (_data, _err, variables) => {
          setPendingItems((prev) => {
            const next = new Set(prev);
            variables.items.forEach((item) => {
              next.delete(item);
            });
            return next;
          });
        },
      }),
    );

  const unconfirmChecklistItem =
    api.accountingPeriod.unconfirmChecklistItem.useMutation(
      useHandleTrpcMutation<
        unknown,
        ApiInputs['accountingPeriod']['unconfirmChecklistItem'],
        PeriodSnapshot
      >()({
        invalidations: periodInvalidations,
        onMutate: async ({ vehicleId, id, item }) => {
          await utils.accountingPeriod.getPeriodById.cancel({ vehicleId, id });
          const snapshot = utils.accountingPeriod.getPeriodById.getData({
            vehicleId,
            id,
          });
          utils.accountingPeriod.getPeriodById.setData(
            { vehicleId, id },
            (old) => {
              if (!old) return old;
              const checklist = { ...old.checklist };
              checklist[item] = {
                ...checklist[item],
                confirmed: false,
                confirmedByLabel: null,
                confirmedBy: null,
                confirmedAt: undefined,
              };
              return { ...old, checklist };
            },
          );
          return { snapshot };
        },
        onError: (err, variables, context) => {
          captureException(err);
          if (context?.snapshot) {
            utils.accountingPeriod.getPeriodById.setData(
              { vehicleId: variables.vehicleId, id: variables.id },
              context.snapshot,
            );
          }
        },
        onSettled: (_data, _err, variables) => {
          setPendingItems((prev) => {
            const next = new Set(prev);
            next.delete(variables.item);
            return next;
          });
        },
      }),
    );

  const getChecked = (key: keyof AccountingPeriodChecklist): boolean => {
    if (isZeroCount(key)) return true;
    return period.checklist[key].confirmed;
  };

  const getConfirmedMeta = (key: keyof AccountingPeriodChecklist) => {
    const item = period.checklist[key];
    if (!item?.confirmedByLabel || !item?.confirmedAt) return undefined;
    return { label: item.confirmedByLabel, at: item.confirmedAt };
  };

  const getCheckboxTooltip = (
    itemKey: keyof AccountingPeriodChecklist,
  ): string | undefined => {
    if (isZeroCount(itemKey)) {
      return ZERO_COUNT_TOOLTIP;
    }
    if (SIDE_PANEL_ONLY_ITEMS.includes(itemKey)) {
      return SIDE_PANEL_ONLY_TOOLTIP;
    }
    return undefined;
  };

  const renderChecklistCheckbox = ({
    itemKey,
    isChecked,
    disabled,
  }: ChecklistCheckboxParams) => {
    const isSidePanelOnly = SIDE_PANEL_ONLY_ITEMS.includes(itemKey);
    const checkbox = (
      <AppCheckbox
        checked={isChecked}
        disabled={disabled || isSidePanelOnly}
        onChange={(e) => handleToggle(itemKey, e.target.checked)}
      />
    );

    const tooltip = getCheckboxTooltip(itemKey);
    if (!tooltip) {
      return checkbox;
    }

    return (
      <Tooltip title={tooltip}>
        <span>{checkbox}</span>
      </Tooltip>
    );
  };

  const handleToggle = (
    item: keyof AccountingPeriodChecklist,
    confirmed: boolean,
  ) => {
    if (!vehicle?._id || !periodId) return;
    if (pendingItems.has(item)) return;

    setPendingItems((prev) => new Set(prev).add(item));
    if (confirmed) {
      confirmChecklistItems.mutate({
        vehicleId: vehicle._id,
        id: periodId,
        items: [item],
        confirmed: true,
      });
    } else {
      unconfirmChecklistItem.mutate({
        vehicleId: vehicle._id,
        id: periodId,
        item,
      });
    }
  };

  const isFirstItemConfirmed = getChecked('bankTransactionsReconciliation');

  const confirmedRequiredCount = requiredItems.filter((i) =>
    getChecked(i.key),
  ).length;

  const completionPercentage = Math.round(
    (confirmedRequiredCount / requiredItems.length) * 100,
  );

  const isChecklistUpdating =
    confirmChecklistItems.isPending || unconfirmChecklistItem.isPending;

  useEffect(() => {
    if (!vehicle?._id || !periodId) return;
    const zeroCountKeys = allChecklistItemDefs
      .filter(
        (item) =>
          checklistCounts[item.key] === 0 &&
          !period.checklist[item.key].confirmed,
      )
      .map((item) => item.key);
    if (zeroCountKeys.length === 0) return;

    setPendingItems((prev) => {
      const next = new Set(prev);
      zeroCountKeys.forEach((key) => next.add(key));
      return next;
    });
    confirmChecklistItems.mutate({
      vehicleId: vehicle._id,
      id: periodId,
      items: zeroCountKeys,
      confirmed: true,
    });
  }, [checklistCounts]);

  return (
    <>
      <Row centered justify="space-between">
        <Typography variant="body2" color="text.secondary">
          Required for period end processing
        </Typography>
        {isApproved ? (
          <Typography variant="body2" color="text.secondary">
            Approved on {period?.approvedAt?.toReadable()}
          </Typography>
        ) : (
          <Row centered spacing="xs">
            <Typography variant="body2" color="text.secondary">
              {completionPercentage}%
            </Typography>
            <CircularProgress
              size={24}
              variant="determinate"
              value={completionPercentage}
            />
          </Row>
        )}
      </Row>
      <Column spacing="sm">
        {requiredItems.map((item, index) => {
          const isChecked = getChecked(item.key);
          const confirmedMeta = isChecked && getConfirmedMeta(item.key);
          const isLocked = index > 0 && !isFirstItemConfirmed;
          return (
            <ChecklistCard
              key={item.key}
              disabled={isLocked}
              icon={<ChecklistItemIcon>{item.icon}</ChecklistItemIcon>}
              primaryLabel={<Typography variant="h6">{item.label}</Typography>}
              secondaryLabel={
                confirmedMeta ? (
                  <Typography variant="body2" color="text.secondary">
                    Completed by {confirmedMeta.label} on{' '}
                    {confirmedMeta.at.toReadable()}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {item.subLabelTemplate(
                      formatCount(
                        checklistCounts,
                        item.key,
                        item.formatValue,
                        vehicle?.baseCurrency,
                      ),
                    )}
                  </Typography>
                )
              }
              actions={renderChecklistCheckbox({
                itemKey: item.key,
                isChecked,
                disabled:
                  isApproved ||
                  isChecklistUpdating ||
                  isLocked ||
                  isZeroCount(item.key),
              })}
              secondaryActions={
                <IconButton
                  size="small"
                  color="default"
                  disabled={isLocked}
                  onClick={
                    item.key === 'accrualsPayablesReceivables'
                      ? () =>
                          onOpenSidebar({
                            type: 'accruals',
                            periodStart: period.periodStart.toString(),
                            periodEnd: period.periodEnd.toString(),
                          })
                      : item.key === 'unrealisedFxGainsAndLosses'
                      ? () =>
                          onOpenSidebar({
                            type: 'unrealisedFxGains',
                            periodStart: period.periodStart.toString(),
                            periodEnd: period.periodEnd.toString(),
                          })
                      : item.key === 'transactionRates'
                      ? () =>
                          onOpenSidebar({
                            type: 'transactionRates',
                            periodStart: period.periodStart.toString(),
                            periodEnd: period.periodEnd.toString(),
                          })
                      : item.key === 'prepaymentResolutions'
                      ? () =>
                          onOpenSidebar({
                            type: 'prepayments',
                            periodStart: period.periodStart.toString(),
                            periodEnd: period.periodEnd.toString(),
                          })
                      : item.key === 'companyValuations'
                      ? () => onOpenSidebar({ type: 'companyValuations' })
                      : item.key === 'bankTransactionsReconciliation'
                      ? () => window.open(BANK_RECONCILIATION, '_blank')
                      : item.key === 'incomeExpensesInvestmentsAndRealisations'
                      ? (e: React.MouseEvent<HTMLElement>) =>
                          setLinksMenuAnchor(e.currentTarget)
                      : item.key === 'bankBalancesStatement'
                      ? () =>
                          onOpenSidebar({
                            type: 'bankBalances',
                          })
                      : undefined
                  }
                >
                  <ArrowForward fontSize="small" />
                </IconButton>
              }
            />
          );
        })}
      </Column>
      <Menu
        anchorEl={linksMenuAnchor}
        open={Boolean(linksMenuAnchor)}
        onClose={() => setLinksMenuAnchor(null)}
      >
        <MenuItem
          component="a"
          href={INCOME_OR_EXPENSE_LIST}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setLinksMenuAnchor(null)}
        >
          Income &amp; Expenses
        </MenuItem>
        <MenuItem
          component="a"
          href={LP_INVESTMENT_LIST}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setLinksMenuAnchor(null)}
        >
          Investments
        </MenuItem>
        <MenuItem
          component="a"
          href={LP_REALISATION_LIST}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setLinksMenuAnchor(null)}
        >
          Realisations
        </MenuItem>
      </Menu>
      <Divider />
      <Typography variant="body2" color="text.secondary">
        Optional reviews
      </Typography>
      <Grid container spacing={2}>
        {optionalItems.map((item) => {
          const isChecked = getChecked(item.key);
          const confirmedMeta = isChecked && getConfirmedMeta(item.key);
          const isLocked = !isFirstItemConfirmed;
          return (
            <Grid key={item.key} size={{ xs: 12, sm: 6 }}>
              <ChecklistCard
                disabled={isLocked}
                icon={<ChecklistItemIcon>{item.icon}</ChecklistItemIcon>}
                primaryLabel={
                  <Typography variant="h6">{item.label}</Typography>
                }
                secondaryLabel={
                  confirmedMeta ? (
                    <Typography variant="body2" color="text.secondary">
                      Completed by {confirmedMeta.label} on{' '}
                      {confirmedMeta.at.toReadable()}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {item.subLabelTemplate(
                        formatCount(
                          checklistCounts,
                          item.key,
                          item.formatValue,
                          vehicle?.baseCurrency,
                        ),
                      )}
                    </Typography>
                  )
                }
                actions={renderChecklistCheckbox({
                  itemKey: item.key,
                  isChecked,
                  disabled:
                    isApproved ||
                    isChecklistUpdating ||
                    isLocked ||
                    isZeroCount(item.key),
                })}
                secondaryActions={
                  <IconButton
                    size="small"
                    color="default"
                    disabled={isLocked}
                    onClick={
                      item.key === 'awaitingCommitments'
                        ? () => window.open(EQUALISATIONS_ADD, '_blank')
                        : undefined
                    }
                  >
                    <OpenInNew fontSize="small" />
                  </IconButton>
                }
              />
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

export default PeriodEndReviewChecklist;
